import { ApexOptions } from 'apexcharts'
import { FC, useMemo } from 'react'
import Chart from 'react-apexcharts'
import { baseChartOptions, CHART_COLORS, CHART_MUTED, formatAxisNumber, INK_MUTED, mergeOptions } from './charts/chartTheme'
import { IChartSeries } from './LineChart'

interface IBarChart {
    categories: string[]
    series: IChartSeries[]
    horizontal?: boolean
    /** Highlight one bar (single-series only); others use the soft accent */
    highlightIndex?: number
    /** Unit for every series (tooltips) */
    unit?: string
    /** Per-series units; overrides `unit` */
    units?: string[]
    xTitle?: string
    yTitle?: string
    /**
     * Two-series charts with different units: plot the second series on its own
     * right-hand y-axis with this title.
     */
    y2Title?: string
    tooltipLabels?: string[]
}

type IYAxis = Exclude<ApexOptions['yaxis'], unknown[] | undefined>

export const BarChart: FC<IBarChart> = ({ categories, series, horizontal = false, highlightIndex, unit = '', units, xTitle, yTitle, y2Title, tooltipLabels }) => {
    const seriesCount = series.length
    const firstName = series[0]?.name
    const secondName = series[1]?.name
    const unitsKey = units?.join('|')
    const options = useMemo<ApexOptions>(() => {
        const highlight = highlightIndex != null && seriesCount === 1
        const dualAxis = !horizontal && !!y2Title && seriesCount === 2
        const unitList = unitsKey?.split('|')
        const unitFor = (seriesIndex?: number) => unitList?.[seriesIndex ?? 0] ?? unit
        const valueFormatter = (value: number, opts?: { seriesIndex: number }) =>
            `${formatAxisNumber(value)} ${unitFor(opts?.seriesIndex)}`.trim()
        const baseYAxis = baseChartOptions.yaxis as IYAxis
        const yaxis: ApexOptions['yaxis'] = dualAxis
            ? [
                mergeOptions({ yaxis: baseYAxis }, { yaxis: { seriesName: firstName, min: 0, forceNiceScale: true, title: { text: yTitle } } }).yaxis as IYAxis,
                mergeOptions({ yaxis: baseYAxis }, { yaxis: { seriesName: secondName, opposite: true, min: 0, forceNiceScale: true, title: { text: y2Title } } }).yaxis as IYAxis,
            ]
            : {
                title: { text: yTitle },
                labels: horizontal
                    ? { formatter: (value: number | string) => String(value), maxWidth: 120, style: { fontSize: '13px', colors: INK_MUTED } }
                    : {},
                ...(horizontal ? {} : { min: 0, forceNiceScale: true }),
            }
        return mergeOptions(baseChartOptions, {
            colors: highlight
                ? categories.map((_, i) => (i === highlightIndex ? CHART_COLORS[0] : CHART_MUTED))
                : CHART_COLORS,
            plotOptions: {
                bar: {
                    horizontal,
                    distributed: highlight,
                    borderRadius: horizontal ? 4 : 6,
                    barHeight: '62%',
                    columnWidth: seriesCount > 1 ? '70%' : '55%',
                },
            },
            legend: { show: seriesCount > 1 },
            grid: horizontal
                ? { xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } }
                : {},
            xaxis: {
                categories,
                title: { text: xTitle },
                labels: horizontal ? { formatter: (value: string) => formatAxisNumber(Number(value)) } : {},
                tickAmount: !horizontal && categories.length > 14 ? 7 : undefined,
            },
            yaxis,
            tooltip: {
                shared: dualAxis ? true : undefined,
                intersect: dualAxis ? false : undefined,
                x: { formatter: (value: number | string, opts?: { dataPointIndex: number }) => (tooltipLabels ?? categories)[opts?.dataPointIndex ?? -1] ?? String(value) },
                y: { formatter: valueFormatter },
            },
        })
    }, [categories, seriesCount, firstName, secondName, horizontal, highlightIndex, unit, unitsKey, xTitle, yTitle, y2Title, tooltipLabels])

    return <Chart options={options} series={series} type='bar' width='100%' height='100%' />
}
